import React, { useState, useMemo, useRef, useEffect } from 'react';
import Header from '../components/layout/Header';
import ErrorBanner from '../components/ui/ErrorBanner';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import KPICard from '../components/ui/KPICard';
import { useCsr } from '../hooks/useCsr';
import { useAuth } from '../context/AuthContext';
import { toLocalDateStr } from '../utils/formatters';

function today() { return toLocalDateStr(new Date()); }
const toStr = toLocalDateStr;

function getMonday() {
  const d = new Date(); const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day)); d.setHours(0,0,0,0);
  return toStr(d);
}
function monthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
}
function lastMonthStart() {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth()-1); return toStr(d);
}
function lastMonthEnd() {
  const d = new Date(); d.setDate(0); return toStr(d);
}

const RANGES = [
  { label: 'Today',      start: today,          end: today },
  { label: 'This Week',  start: getMonday,       end: today },
  { label: 'This Month', start: monthStart,      end: today },
  { label: 'Last Month', start: lastMonthStart,  end: lastMonthEnd },
  { label: 'YTD',        start: () => `${new Date().getFullYear()}-01-01`, end: today },
  { label: 'Custom',     start: null,            end: null },
];

function fmtDuration(secs) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60), s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function RateBadge({ rate }) {
  const bg    = rate >= 60 ? '#dcfce7' : rate >= 40 ? '#fef3c7' : '#fee2e2';
  const color = rate >= 60 ? '#16a34a' : rate >= 40 ? '#d97706' : '#dc2626';
  return (
    <span style={{ padding: '0.2rem 0.55rem', borderRadius: '999px', background: bg, color, fontWeight: '700', fontSize: '0.78rem' }}>
      {rate}%
    </span>
  );
}

export default function CsrPage() {
  const { myAccounts } = useAuth();
  const [rangeIdx, setRangeIdx] = useState(0);
  const [customStart, setCustomStart] = useState(today());
  const [customEnd, setCustomEnd] = useState(today());
  const [selectedCsrs, setSelectedCsrs] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sortBy, setSortBy] = useState('totalBooked');
  const [sortDir, setSortDir] = useState('desc');
  const dropdownRef = useRef(null);

  const isCustom = rangeIdx === RANGES.length - 1;

  const currentAccount = myAccounts?.currentAccountId === 999 
    ? { name: 'Sparticus & Friends' } 
    : myAccounts?.accounts?.find(a => a.id === myAccounts?.currentAccountId);

  const { start, end } = useMemo(() => {
    if (isCustom) return { start: customStart, end: customEnd };
    const r = RANGES[rangeIdx];
    return { start: r.start(), end: r.end() };
  }, [rangeIdx, isCustom, customStart, customEnd]);

  const { csrs = [], totals = {}, warnings = [], loading, error } = useCsr(start, end);

  const csrNames = useMemo(() => {
    // Server already restricts csrs to the CSR allowlist (routes/csr.js) - no extra
    // client-side exclusion needed here.
    const names = [...new Set(csrs.map(c => c.name))].filter(Boolean);
    return names.sort();
  }, [csrs]);

  const toggleCsr = (name) => {
    setSelectedCsrs(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const filtered = useMemo(() => {
    if (selectedCsrs.length === 0) return csrs;
    return csrs.filter(c => selectedCsrs.includes(c.name));
  }, [csrs, selectedCsrs]);

  const displayTotals = useMemo(() => {
    if (selectedCsrs.length === 0) return totals;
    // Recalculate totals for selected CSRs
    const t = {
      totalCalls: 0, stBooked: 0, stInbound: 0, stOutbound: 0, stAbandoned: 0, stHandled: 0,
      ghlInbound: 0, ghlOutbound: 0, ghlAppts: 0, totalBooked: 0,
      outboundBooked: 0, hlBooked: 0,
      ductCleaning: 0, membershipsSold: 0, addOnsSold: 0
    };
    filtered.forEach(c => {
      // ServiceTitan only - HL calls are forwards of the same calls, so adding them double counts.
      // ST calls + HL outbound (HL inbound is a forward of a call ST already logs).
      t.totalCalls += (c.stInbound + c.stOutbound + (c.ghlOutbound || 0));
      t.totalOutbound = (t.totalOutbound || 0) + c.stOutbound + (c.ghlOutbound || 0);
      t.stBooked += c.stBooked;
      t.stInbound += c.stInbound;
      t.stHandled += c.stInbound; // a CSR's ST inbound calls are the ones they answered
      t.stOutbound += c.stOutbound;
      // Abandoned calls never reach a CSR - keep the overall figure when filtering by CSR.
      t.stAbandoned = totals.stAbandoned || 0;
      t.ghlInbound += c.ghlInbound;
      t.ghlOutbound += c.ghlOutbound;
      t.outboundBooked += (c.outboundBooked || 0);
      t.hlBooked += (c.hlBooked || 0);
      t.totalBooked += c.totalBooked;
      t.ductCleaning += (c.ductCleaning || 0);
      t.membershipsSold += (c.membershipsSold || 0);
      t.addOnsSold += (c.addOnsSold || 0);

      // Aggregate bookingsByBU if it exists on individual CSR data
      if (c.bookingsByBU) {
        if (!t.bookingsByBU) t.bookingsByBU = {};
        Object.entries(c.bookingsByBU).forEach(([name, count]) => {
          t.bookingsByBU[name] = (t.bookingsByBU[name] || 0) + count;
        });
      }
    });

    if (t.bookingsByBU) {
      t.bookingsByBU = Object.entries(t.bookingsByBU)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    }
    // Union (not sum) of the selected CSRs' caller IDs - a caller who talked to two
    // different selected CSRs should still only count once.
    const uniqueSet = new Set();
    filtered.forEach(c => (c.callerIds || []).forEach(id => uniqueSet.add(id)));
    t.uniqueCallers = uniqueSet.size;

    // First Call Resolution: sum each caller's call count ACROSS the selected CSRs first
    // (a caller who called two different selected CSRs made 2 calls total, not 1+1 separate
    // "one call" callers), then find what % had a combined total of exactly 1.
    const callTotals = new Map();
    filtered.forEach(c => (c.callerCounts || []).forEach(([id, count]) => {
      callTotals.set(id, (callTotals.get(id) || 0) + count);
    }));
    let resolvedFirstCall = 0;
    for (const count of callTotals.values()) if (count === 1) resolvedFirstCall++;
    t.firstCallResolutionRate = callTotals.size > 0 ? Math.round((resolvedFirstCall / callTotals.size) * 100) : 0;

    // Add rates
    t.inboundBookingRate = t.stInbound > 0 ? Math.round(t.stBooked / t.stInbound * 100) : 0;
    t.outboundBookingRate = t.stOutbound + t.ghlOutbound > 0 ? Math.round((t.outboundBooked + t.hlBooked) / (t.stOutbound + t.ghlOutbound) * 100) : 0;
    return t;
  }, [filtered, totals, selectedCsrs]);

  return (
    <div>
      <Header title={currentAccount?.name || 'CSR Dashboard'} />
      <div style={st.page}>
        <ErrorBanner message={error} />
        {warnings.length > 0 && (
          <div style={st.warningBanner}>
            {warnings.map((w, i) => <div key={i}>⚠️ {w}</div>)}
          </div>
        )}

        {/* Toolbar */}
        <div style={st.toolbar}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {RANGES.map((r, i) => (
              <button key={r.label} onClick={() => setRangeIdx(i)}
                style={{ ...st.rangeBtn, ...(rangeIdx === i ? st.rangeBtnActive : {}) }}>
                {r.label}
              </button>
            ))}
            {isCustom && (
              <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={st.dateInput} />
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>→</span>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={st.dateInput} />
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginLeft: 'auto', flexWrap: 'wrap' }}>
            {/* Multi-select CSR dropdown */}
            <div ref={dropdownRef} style={{ position: 'relative' }}>
              <button onClick={() => setDropdownOpen(o => !o)} style={st.multiSelectBtn}>
                {selectedCsrs.length === 0 ? 'All CSRs' : selectedCsrs.length === 1 ? selectedCsrs[0] : `${selectedCsrs.length} CSRs selected`}
                <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem' }}>▼</span>
              </button>
              {dropdownOpen && csrNames.length > 0 && (
                <div style={st.multiSelectMenu}>
                  <div style={st.multiSelectItem} onClick={() => setSelectedCsrs([])}>
                    <span style={{ ...st.checkbox, background: selectedCsrs.length === 0 ? '#6366f1' : '#fff', borderColor: selectedCsrs.length === 0 ? '#6366f1' : '#d1d5db' }}>
                      {selectedCsrs.length === 0 && <span style={st.checkmark}>✓</span>}
                    </span>
                    All CSRs
                  </div>
                  {csrNames.map(name => (
                    <div key={name} style={st.multiSelectItem} onClick={() => toggleCsr(name)}>
                      <span style={{ ...st.checkbox, background: selectedCsrs.includes(name) ? '#6366f1' : '#fff', borderColor: selectedCsrs.includes(name) ? '#6366f1' : '#d1d5db' }}>
                        {selectedCsrs.includes(name) && <span style={st.checkmark}>✓</span>}
                      </span>
                      {name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{start} → {end}</span>
          </div>
        </div>

        {/* Section 1: Call Volume */}
        <div style={st.sectionHeader}>Call Volume & Activity</div>
        <div style={st.kpiRow}>
          <KPICard title="Total Calls"        value={displayTotals.totalCalls    ?? 0} icon="📞" color="#475569" loading={loading} sub="ST calls + HL outbound" />
          <KPICard title="Total Calls Handled" value={displayTotals.stHandled ?? 0} icon="🎧" color="#0d9488" loading={loading} sub="ST inbound answered by a CSR" />
          <KPICard title="Total Outbound"     value={displayTotals.totalOutbound ?? 0} icon="📤" color="#7c3aed" loading={loading} sub="ST + HL outbound" />
          <KPICard title="Unique Callers"     value={displayTotals.uniqueCallers ?? 0} icon="👤" color="#0f766e" loading={loading} sub="unique contacts who called" />
          <KPICard title="First Call Resolution" value={`${displayTotals.firstCallResolutionRate ?? 0}%`} icon="🎯" color="#0d9488" loading={loading} sub="callers who only called once" />
          <KPICard title="ST Inbound"         value={displayTotals.stInbound     ?? 0} icon="📥" color="#3b82f6" loading={loading} sub="ServiceTitan calls" />
          <KPICard title="ST Outbound"        value={displayTotals.stOutbound    ?? 0} icon="📤" color="#8b5cf6" loading={loading} sub="ServiceTitan calls" />
          <KPICard title="HL Inbound"         value={displayTotals.ghlInbound    ?? 0} icon="📥" color="#0891b2" loading={loading} sub="HighLevel inbound calls" />
          <KPICard title="HL Outbound"        value={displayTotals.ghlOutbound   ?? 0} icon="📤" color="#6366f1" loading={loading} sub="HighLevel outbound calls" />
          <KPICard title="Abandoned"          value={displayTotals.stAbandoned   ?? 0} icon="❌" color="#ef4444" loading={loading} sub="ST inbound - agent handled - HL inbound" />
        </div>

        {/* Section 2: Bookings & Performance */}
        <div style={st.sectionHeader}>Outcomes & Conversion</div>
        <div style={st.kpiRow}>
          <KPICard title="Total Booked"       value={displayTotals.totalBooked   ?? 0} icon="📈" color="#1e293b" loading={loading} sub="ST Booked + Outbound + HL Bookings" />
          <KPICard title="ST Booked"          value={displayTotals.stBooked      ?? 0} icon="✅" color="#22c55e" loading={loading} sub="booked on an inbound ST call" />
          <KPICard title="Outbound Bookings"   value={displayTotals.outboundBooked ?? 0} icon="📝" color="#0ea5e9" loading={loading} sub="CSR bookings not from an inbound call" />
          <KPICard title="HL Bookings"        value={displayTotals.hlBooked      ?? 0} icon="📅" color="#ec4899" loading={loading} sub="ST jobs booked by Sparticus & Friends" />
          <KPICard title="Duct Cleaning"      value={displayTotals.ductCleaning}       icon="🧹" color="#06b6d4" loading={loading} sub="DCLEAN job type" />
          <KPICard title="Memberships Sold"   value={displayTotals.membershipsSold ?? 0} icon="🪪" color="#059669" loading={loading} sub="sold in period" />
          <KPICard title="Add-Ons Sold"       value={displayTotals.addOnsSold ?? 0} icon="➕" color="#f59e0b" loading={loading} sub="membership sold to existing member" />
          <KPICard title="Inbound Rate"       value={`${displayTotals.inboundBookingRate ?? 0}%`} icon="🎯" color="#0ea5e9" loading={loading} sub="ST booked / ST inbound" />
          <KPICard title="Outbound Rate"      value={`${displayTotals.outboundBookingRate ?? 0}%`} icon="📊" color="#7c3aed" loading={loading} sub="ST outbound booked / ST outbound calls" />
        </div>

        {/* Section 3: Business Units */}
        {displayTotals.bookingsByBU?.length > 0 && (
          <>
            <div style={st.sectionHeader}>Business Unit Bookings</div>
            <div style={st.kpiRow}>
              {displayTotals.bookingsByBU.map(bu => (
                <KPICard key={bu.name} title={bu.name} value={bu.count} icon="🏢" color="#7c3aed" loading={loading} sub="confirmed bookings" />
              ))}
            </div>
          </>
        )}

        {/* Per-CSR table */}
        {loading ? <LoadingSpinner /> : (
          (() => {
            const rows = [...filtered].sort((a, b) => {
              const av = a[sortBy] ?? 0, bv = b[sortBy] ?? 0;
              if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
              return sortDir === 'asc' ? av - bv : bv - av;
            });

            return rows.length === 0 ? (
              <div style={st.empty}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎧</div>
                <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>No CSR data for this period</div>
                <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                  Calls are pulled live from ServiceTitan telecom. Manual bookings come from ST jobs. HighLevel bookings come from GHL opportunities.
                </div>
              </div>
            ) : (
              <div style={st.tableWrap}>
                <table style={st.table}>
                  <thead>
                    <tr style={st.thead}>
                      {[
                        ['CSR Name','name'],['ST Inbound','stInbound'],['ST Outbound','stOutbound'],
                        ['ST Booked','stBooked'],['Outbound Bookings','outboundBooked'],
                        ['HL Inbound','ghlInbound'],['HL Outbound','ghlOutbound'],
                        ['HL Bookings','hlBooked'],['Total Booked','totalBooked'],['Duct Cleaning','ductCleaning'],
                        ['Memberships Sold','membershipsSold'],['Add-Ons Sold','addOnsSold'],
                        ['Inbound Rate','inboundRate'],['Outbound Rate','outboundRate'],['Avg Duration','avgDuration'],
                      ].map(([label, key]) => (
                        <th key={key} style={{ ...st.th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => { if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortBy(key); setSortDir('desc'); } }}>
                          {label}{sortBy === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((csr, i) => (
                      <tr key={csr.name}
                        style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', cursor: 'pointer' }}
                        onClick={() => toggleCsr(csr.name)}
                      >
                        <td style={{ ...st.td, fontWeight: '600', color: '#1e293b' }}>
                          <div style={st.csrName}>
                            <div style={{
                              ...st.csrAvatar,
                              background: selectedCsrs.includes(csr.name) ? '#4338ca' : '#6366f1'
                            }}>
                              {csr.name[0]?.toUpperCase() || '?'}
                            </div>
                            {csr.name}
                          </div>
                        </td>
                        <td style={st.td}>{csr.stInbound}</td>
                        <td style={st.td}>{csr.stOutbound}</td>
                        <td style={{ ...st.td, color: '#16a34a', fontWeight: '600' }}>{csr.stBooked}</td>
                        <td style={{ ...st.td, color: '#d97706', fontWeight: '600' }}>{csr.outboundBooked}</td>
                        <td style={{ ...st.td, color: '#0891b2', fontWeight: '600' }}>{csr.ghlInbound}</td>
                        <td style={{ ...st.td, color: '#6366f1', fontWeight: '600' }}>{csr.ghlOutbound}</td>
                        <td style={{ ...st.td, color: '#ec4899', fontWeight: '600' }}>{csr.hlBooked}</td>
                        <td style={{ ...st.td, fontWeight: '800', color: '#1e293b' }}>{csr.totalBooked}</td>
                        <td style={{ ...st.td, color: '#0891b2', fontWeight: '600' }}>{csr.ductCleaning || 0}</td>
                        <td style={{ ...st.td, color: '#059669', fontWeight: '600' }}>{csr.membershipsSold || 0}</td>
                        <td style={{ ...st.td, color: '#f59e0b', fontWeight: '600' }}>{csr.addOnsSold || 0}</td>
                        <td style={st.td}><RateBadge rate={csr.inboundRate} /></td>
                        <td style={st.td}><RateBadge rate={csr.outboundRate} /></td>
                        <td style={{ ...st.td, color: '#64748b' }}>{fmtDuration(csr.avgDuration)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {selectedCsrs.length > 0 && (
                  <div style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', color: '#6366f1', borderTop: '1px solid #e0e7ff' }}>
                    Filtered to: <strong>{selectedCsrs.join(', ')}</strong> — <button onClick={() => setSelectedCsrs([])} style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontWeight: '600', padding: 0 }}>Clear filter ✕</button>
                  </div>
                )}
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
}

const st = {
  page: { padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  warningBanner: {
    background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
    borderRadius: '0.75rem', padding: '0.6rem 1rem', fontSize: '0.8rem',
    display: 'flex', flexDirection: 'column', gap: '0.25rem',
  },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
    background: '#fff', padding: '0.75rem 1rem', borderRadius: '0.75rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  rangeBtn: {
    padding: '0.4rem 0.8rem', background: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600',
    color: '#475569', transition: 'all 0.15s',
  },
  rangeBtnActive: { background: '#6366f1', color: '#fff', border: '1px solid #6366f1' },
  dateInput: {
    padding: '0.35rem 0.5rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem',
    fontSize: '0.78rem', color: '#374151', background: '#fff', outline: 'none', cursor: 'pointer',
  },
  multiSelectBtn: {
    padding: '0.4rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem',
    fontSize: '0.8rem', color: '#374151', background: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '0.25rem', whiteSpace: 'nowrap',
  },
  multiSelectMenu: {
    position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100,
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: '0.5rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '180px', maxHeight: '260px',
    overflowY: 'auto',
  },
  multiSelectItem: {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem', color: '#374151',
    userSelect: 'none',
  },
  checkbox: {
    width: '1rem', height: '1rem', borderRadius: '0.25rem', border: '1.5px solid #d1d5db',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  checkmark: { color: '#fff', fontSize: '0.65rem', fontWeight: '700', lineHeight: 1 },
  kpiRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  tableWrap: {
    background: '#fff', borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '900px' },
  thead: { background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)' },
  th: {
    padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: '700',
    color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: '1px solid #c7d2fe', whiteSpace: 'nowrap',
  },
  td: { padding: '0.7rem 1rem', fontSize: '0.875rem', color: '#374151', borderBottom: '1px solid #f1f5f9' },
  csrName: { display: 'flex', alignItems: 'center', gap: '0.6rem', whiteSpace: 'nowrap' },
  csrAvatar: {
    width: '1.875rem', height: '1.875rem', borderRadius: '50%', background: '#6366f1',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: '700', fontSize: '0.75rem', flexShrink: 0,
  },
  sectionHeader: {
    fontSize: '0.75rem', fontWeight: '800', color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    marginTop: '0.5rem', marginBottom: '-0.25rem'
  },
  empty: {
    background: '#fff', borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: '3rem', textAlign: 'center', color: '#64748b',
  },
};
